import { useEffect, useState } from "react";
import { Layout } from "providers/Layout";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  CheckboxCard,
  Input
} from "shared/components";
import { usePlacesWidget } from "react-google-autocomplete";
import { Link, useNavigate } from "react-router-dom";
import { ChevronDown, ChevronUp, Plus } from "untitledui-js-base";
import addressApi from "api/landlord/address";
import { getErrorMessage } from "utils/error";

import { googlePlaceToAddress } from "shared/addressUtils";
import config from "config/config";
import { useAuthContext } from "providers/Auth";
import { UnitForms } from "shared/app/UnitsTableView/components/UnitForms";
import { blankUnit } from "shared/app/UnitsTableView/constants";
import { useVerificationTemplatesController } from "views/verification-templates/controller";
import { Select } from "shared/components";
import { states } from "utils/stateCodes";
import { BreadCrumbs } from "shared/components/BreadCrumbs";
import { TemplateReviewModal } from "shared/app/VerificationTemplate/modals/TemplateReviewModal";
import { Loader } from "shared/components/Loader";
import { Unit } from "shared/types";
import { Redirect } from "shared/components/Redirect";
import { toast } from "react-toastify";

export const AddProperty = () => {
  const [address, setAddress] = useState({
    address1: "",
    address2: "",
    postal_code: "",
    city: "",
    state_or_province: "",
    country: ""
  });
  const navigate = useNavigate();
  const { type, permissions } = useAuthContext();
  const [propertyName, setPropertyName] = useState<string>("");
  const [unitForms, setUnitForms] = useState([blankUnit]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>();
  const [showReviewModal, setShowReviewModal] = useState<boolean>(false);
  const [showMoreSettings, setShowMoreSettings] = useState<boolean>(false);

  const [propertyNameError, setPropertyNameError] = useState("");

  const { templates, isFetching } = useVerificationTemplatesController(999);

  const sortedTemplates = [...(templates || [])].sort(
    (a, b) => Number(b.default) - Number(a.default)
  );

  const renderedTemplates = showMoreSettings
    ? sortedTemplates
    : sortedTemplates.slice(0, 7);

  const { ref } = usePlacesWidget({
    apiKey: config.placesApiKey,
    onPlaceSelected: (place) => {
      const addressFields = googlePlaceToAddress(place);
      setAddress({
        address1: addressFields?.address1 || "",
        address2: address.address2,
        postal_code: addressFields?.postalCode,
        city: addressFields?.city,
        state_or_province: addressFields?.stateOrProvince,
        country: addressFields?.country
      });
    },
    options: {
      types: ["street_address"],
      fields: ["formatted_address", "address_components"],
      componentRestrictions: { country: "us" }
    }
  });

  const addUnitForm = () => {
    setUnitForms([...unitForms, blankUnit]);
  };

  const updateUnitForm = (index, field, value) => {
    setUnitForms((prev) => {
      const forms = [...prev];

      forms[index] = {
        ...forms[index],
        [field]: value
      };

      return forms;
    });
  };

  useEffect(() => {
    if (templates && templates.length)
      setSelectedTemplateId(templates.find((t) => t.default)?.id);
  }, [templates]);

  if (type.isSDK || !permissions.canManageProperties)
    return <Redirect href="/" />;

  const addProperty = async () => {
    const unitsToAdd = unitForms.reduce((acc, unit) => {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (unit.unitName && unit.rentAmount !== null)
        acc.push({
          title: unit.unitName,
          monthly_rent_amount: {
            cents: unit.rentAmount * 100,
            currency: "USD"
          },
          available_at: unit.available_at,
          occupied: unit.occupied
        } as Unit);

      return acc;
    }, [] as Unit[]);
    if (propertyName)
      try {
        await addressApi.addProperty(
          propertyName,
          address,
          // @ts-expect-error -- wrong type
          unitsToAdd,
          selectedTemplateId
        );
        navigate("/properties");
      } catch (err) {
        toast.error(getErrorMessage(err));
      }
    else {
      setPropertyNameError("Property name and address cannot be empty");
      toast.error("Property name and address cannot be empty");
    }
  };

  return (
    <Layout
      header={
        <div className="flex gap-2.5 p-3">
          <BreadCrumbs
            crumbs={[
              {
                title: "Properties",
                href: "/properties"
              },
              {
                title: "Add property",
                href: "/properties/add"
              }
            ]}
          />
        </div>
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button
            variant={ButtonVariant.BASE}
            size={ButtonSize.SM}
            onClick={() => navigate(-1)}
            className="w-[125px]"
          >
            Cancel
          </Button>
          <Button
            variant={ButtonVariant.PRIMARY}
            size={ButtonSize.SM}
            onClick={addProperty}
            className="w-[125px]"
            disabled={
              // disabled if any unit form is empty
              !unitForms[0]?.unitName || !unitForms[0]?.rentAmount
            }
          >
            Add
          </Button>
        </div>
      }
    >
      <h3 className="mb-3 text-2xl font-bold">Add property</h3>
      <p className="mb-6 text-sm text-content-400">You can add new property</p>
      <form className="w-full max-w-[760px]">
        <div className="flex gap-4">
          <div className="w-1/2">
            <Input
              id="propertyName"
              error={propertyNameError}
              label="Property name"
              required
              placeholder="Property name"
              onChange={(e) => {
                setPropertyNameError("");
                setPropertyName(e.target.value);
              }}
            />
          </div>
        </div>
        <div className="my-6 border-t border-alpha-80" />
        <div className="grid grid-cols-2 gap-4 ">
          <div className="w-full">
            <Input
              id="selectedPlace"
              label="Street Address"
              required
              placeholder="2972 Westheimer Rd"
              // @ts-expect-error
              ref={ref}
              value={address.address1}
              onChange={(e) =>
                setAddress((prev) => ({ ...prev, address1: e.target.value }))
              }
            />
          </div>
          <div className="w-full">
            <label className="mb-1 block text-sm font-[500] text-content-700">
              Apartment, suite, etc.
              <span className="ml-1 text-content-100">(optional)</span>
            </label>
            <Input
              id="address2"
              name="address2"
              placeholder="Apartment 12"
              value={address.address2}
              onChange={(e) =>
                setAddress((prev) => ({
                  ...prev,
                  [e.target.id]: e.target.value
                }))
              }
            />
          </div>
          <div className="w-full">
            <Input
              id="city"
              name="city"
              label="City"
              required
              placeholder="Austin"
              value={address.city}
              onChange={(e) =>
                setAddress((prev) => ({
                  ...prev,
                  [e.target.id]: e.target.value
                }))
              }
            />
          </div>
          <div className="flex w-full justify-end gap-4">
            <div className="mb-2 mt-auto h-fit flex-1">
              <Select
                id="state_or_province"
                label="State"
                placeholder="State"
                value={address.state_or_province}
                searchable
                options={states}
                hideOptional
                onChange={(e) =>
                  setAddress((prev) => ({
                    ...prev,
                    state_or_province: e.target.value
                  }))
                }
              />
            </div>
            <div className="flex-1">
              <Input
                id="postal_code"
                name="postalCode"
                required
                label="Zip"
                placeholder="11111"
                value={address.postal_code}
                onChange={(e) =>
                  setAddress((prev) => ({
                    ...prev,
                    [e.target.id]: e.target.value
                  }))
                }
              />
            </div>
          </div>
        </div>
        <div className="my-6 border-t border-alpha-80" />
        {type.isBoomScreen ? (
          <>
            <p className="mb-4 text-base font-bold">Screening settings</p>

            {templates && !templates.length ? (
              <p className="mb-2 text-sm text-content-500">
                Hm.. Looks like you don&apos;t have any templates. Create one
              </p>
            ) : null}
            <div className="mb-6 flex flex-wrap gap-4">
              {isFetching ? (
                <div className="mx-10 flex min-h-[106px] items-center justify-center">
                  <Loader />
                </div>
              ) : (
                <>
                  <Link to="/verification-templates?create-mode=true">
                    <CheckboxCard
                      checked={false}
                      id="create-template"
                      wrapperClassName="border-primary-500 border-dashed bg-primary-20 hover:bg-primary-50 rounded-lg"
                      label={
                        <p className="w-full text-center text-sm font-medium text-primary-500">
                          Create template
                        </p>
                      }
                      name="create-template"
                      onChange={() => {}}
                      className="flex min-h-[76px] w-[176px] flex-col items-center  justify-center gap-2 rounded-lg border-2 border-dashed border-primary-500 bg-primary-20 hover:bg-primary-50"
                    />
                  </Link>
                  {renderedTemplates.map((template) => (
                    <CheckboxCard
                      key={template.id}
                      checked={selectedTemplateId === template.id}
                      id={template.id}
                      wrapperClassName="border-alpha-100 animate-appear"
                      label={
                        <div className="relative w-full">
                          {template.default ? (
                            <p className="absolute -right-4 -top-4 rounded-bl-lg rounded-tr-xl border-b border-l border-alpha-100 bg-white px-2 text-xs font-medium">
                              Default
                            </p>
                          ) : null}
                          <p className="w-full overflow-hidden text-ellipsis whitespace-nowrap break-all text-sm font-medium text-content-600">
                            {template.name}
                          </p>
                          <Button
                            variant={ButtonVariant.TEXT}
                            onClick={() => {
                              setSelectedTemplateId(template.id);
                              setShowReviewModal(true);
                            }}
                          >
                            Review
                          </Button>
                        </div>
                      }
                      name="name"
                      onChange={() => setSelectedTemplateId(template.id)}
                      className="flex max-h-[76px] w-[176px] justify-start"
                    />
                  ))}
                </>
              )}
            </div>
            {(templates?.length || 0) < 7 ? null : (
              <div className="flex justify-center">
                <Button
                  Icon={showMoreSettings ? ChevronUp : ChevronDown}
                  variant={ButtonVariant.TEXT}
                  className="flex !stroke-content-500 !text-content-500"
                  onClick={() => {
                    setShowMoreSettings((prev) => !prev);
                  }}
                >
                  {showMoreSettings ? "Show less" : "Show more"}
                </Button>
              </div>
            )}
            <div className="my-6 border-t border-alpha-80" />
          </>
        ) : null}
        <p className="mb-2.5 text-base font-bold">Units</p>
        <div className="">
          <UnitForms
            unitForms={unitForms as any[]}
            updateUnitForm={updateUnitForm}
            removeUnitForm={(idx) =>
              setUnitForms((prev) => prev.filter((_, index) => idx !== index))
            }
          />
        </div>
        <Button
          variant={ButtonVariant.BASE}
          size={ButtonSize.SM}
          onClick={addUnitForm}
          Icon={Plus}
        >
          Add unit
        </Button>
      </form>
      <TemplateReviewModal
        opened={showReviewModal}
        onClose={() => setShowReviewModal(false)}
        id={selectedTemplateId}
      />
    </Layout>
  );
};
